import { Borrower } from 'app/modules/borrower/models/borrower/borrower.model';
import { Component, OnInit, ViewChild, Input, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Document } from 'app/models/dto/document.model';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DocumentsComponent } from 'app/documents/documents/documents.component';
import { DocumentsService } from 'app/services/documents/documents.service';
import {
    UpdateDocument,
    UpdateDocumentDrsVerification,
    UploadDocument,
    SubscribeToBorrowerDocuments,
    GetViewableMimetypes,
    GetDocumentCategories,
    UpdateDocumentAlert,
    RunExperianCommercial,
    UploadAdvDocument
} from 'app/store/documents/document.actions';
import { DocumentState } from 'app/store/documents/document.state';
import { Observable, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { Store, Select } from '@ngxs/store';
import { map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import * as _ from 'lodash';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { ContactState } from 'app/store/contact/contact.state';
import { Contact, NewContact } from 'app/models/dto/contact.model';
import { GetContactsForBorrower } from 'app/store/contact/contact.actions';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { Alert } from 'app/models/dto/alert.model';
import { DismissOcrolusAlert, GetOcrolusActivity, SendToOcrolus } from 'app/store/ocrolus/ocrolus.actions';
import { OcrolusState } from 'app/store/ocrolus/ocrolus.state';
import { AdvancedFileUploadDialogComponent } from '../document-upload-dialog/advanced-file-upload-dialog.component';
import { documentsAllowedUploadFileTypes } from '../document-upload-dialog/documents-allowed-upload-file-types';
import { FileWithCategory } from '../document-upload-dialog/advanced-file-upload.component';
import { DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING } from '../document-upload-dialog/document-upload-constants';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-documents-smart',
    templateUrl: './documents-smart.component.html',
    styleUrls: ['./documents-smart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DocumentsSmartComponent implements OnInit {
    store = inject(Store);
    documents$: Observable<Document[]> = this.store.select(DocumentState.documents);
    documentCategories$: Observable<DocumentCategory[]> = this.store.select(DocumentState.documentCategories);
    viewableMimetypes$: Observable<any> = this.store.select(DocumentState.viewableMimetypes);
    documentAlert$: Observable<Alert | null> = this.store.select(DocumentState.alert);
    isUploading$: Observable<boolean> = this.store.select(DocumentState.isUploading);
    lenderUser$: Observable<LenderUser> = this.store.select(LenderUserState.lenderUser);
    lenderUser = this.store.selectSignal(LenderUserState.lenderUser);
    ocrolusAlert$: Observable<Alert | null> = this.store.select(OcrolusState.ocrolusAlert);
    sending$: Observable<boolean> = this.store.select(OcrolusState.sending);
    contacts$: Observable<(Contact | NewContact)[]> = this.store.select(ContactState.contacts);

    @Input() compactView: boolean = false;
    @Input() borrower: Borrower;
    @Input() tableColumns: string[] = [
        'select',
        'title',
        'category',
        'for',
        'createdTimestamp',
        'scan-status',
        'action'
    ];
    @Input() showActions: boolean = true;
    @Input() showUploadedBy: boolean = true;
    @Input() defaultPageSize: number = 100;
    @Input() showPageSizeOptions: boolean = true;

    @ViewChild('documentsComponent') documentsComponent: DocumentsComponent;

    canProcessDocs$: Observable<boolean>;
    canUseDRSDocumentVerify$: Observable<boolean>;
    isFundingDesk$: Observable<boolean>;
    destroyed$ = new Subject<void>();
    isPullingCredit: boolean;
    uploadedPercent = 0;
    uploadProgress: string;
    ocrolusEligible: boolean;
    canSoftPullCredit$: Observable<boolean>;
    borrowerBS: BehaviorSubject<Borrower>;
    creditPullDisableMessage: string;
    viewableDocuments$: Observable<Document[]>;  // Filtered for sesitive docs (KYC/KYB)
    documentCategories: DocumentCategory[];

    ocrolusAlert: {
        type: string;
        message: string;
    } | undefined | null = null;

    creditAlert: {
        level: string;
        message: string;
    } | null = null;

    constructor(
        private documentsService: DocumentsService,
        private cdRef: ChangeDetectorRef,
        private dialogRef: MatDialog,
    ) {
        this.documentCategories$.pipe(takeUntilDestroyed()).subscribe({
            next: categories => {
                this.documentCategories = categories;
            },
            error: (error) => {
                console.error('Error in documentCategories subscription:', error);
            }
        });

        if (this.lenderUser().permissions.includes('eaPipelineDocumentContacts')) {
            this.tableColumns.splice(2, 0, 'contact');
        }
    }

    ngOnInit() {
        // Currently going to allow all mimeTypes to be viewable --- might need to do further cleanup on this later
        this.store.dispatch(new GetViewableMimetypes());
        this.store.dispatch(new SubscribeToBorrowerDocuments(this.borrower.id));
        this.store.dispatch(new GetDocumentCategories());
        this.store.dispatch(new GetContactsForBorrower(this.borrower.id));

        this.viewableDocuments$ = combineLatest([
            this.lenderUser$,
            this.documents$
        ]).pipe(
            // eslint-disable-next-line max-len
            map(([lenderUser, documents]) => lenderUser.permissions.includes('canSeeDocValidation') ? documents : documents.filter(doc => doc['category'] !== 'kycKybValidation'))
        );

        this.viewableDocuments$.subscribe({
            next: documents => {
                if (documents.length > 0) {
                    this.handleCheckOcrolusStatus(documents);
                }
            },
            error: (error) => {
                console.error('Error in viewableDocuments subscription:', error);
            }
        });

        this.borrowerBS = new BehaviorSubject(this.borrower);
        this.canSoftPullCredit$ = combineLatest([
            this.lenderUser$,
            this.borrowerBS
        ]).pipe(
            map(([lenderUser, borrower]) => this.softPullCreditEnabled(lenderUser.permissions.includes('softPullCredit'), borrower))
        );
        this.cdRef.markForCheck();

        this.canProcessDocs$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.permissions.includes('submitToOcrolus'))
        );

        this.canUseDRSDocumentVerify$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.permissions.includes('canUseDRSDocumentVerify'))
        );

        this.isFundingDesk$ = this.lenderUser$.pipe(
            map(currentUser => currentUser.roles.includes('fundingDesk'))
        );

        if (!this.borrower.creditPullStale) {
            this.creditPullDisableMessage = 'Credit has been pulled within the last 30 days';
        } else if (!this.borrower.applicationSignedRecently) {
            this.creditPullDisableMessage = 'Application has not been signed within the last 30 days';
        } else if (environment.TENANT_CREDIT_BUTTON_DISABLE) {
            this.creditPullDisableMessage = 'Pull Credit disabled by env variable';
        }
    }

    softPullCreditEnabled(isPermitted: boolean, borrower: Borrower): boolean {
        return (isPermitted && borrower.creditPullStale && borrower.applicationSignedRecently);
    }

    handleUpdateDocument(document: Document): void {
        this.store.dispatch(new UpdateDocument(document));
    }

    handleUpdateDocumentDrsVerification(document: Document): void {
        this.store.dispatch(new UpdateDocumentDrsVerification(document));
    }


    handleProcessDocuments(docIds: number[]): void {
        this.store.dispatch(new SendToOcrolus(this.borrower.id, docIds, true));
    }

    handleUploadDocument(documents: FileList) {
        let totalUploaded = 0;
        _.forEach(documents, (document => {
            this.uploadProgress = 'Uploading ' + totalUploaded + ' of ' + documents.length;
            this.store.dispatch(new UploadDocument(document, this.borrower.id))
                .subscribe({
                    next: () => {
                        totalUploaded++;
                        this.uploadProgress = 'Uploading ' + totalUploaded + ' of ' + documents.length;
                        this.uploadedPercent = totalUploaded / documents.length * 100;
                        if (totalUploaded === documents.length) {
                            this.uploadedPercent = 0;
                        }
                    },
                    error: (error) => {
                        console.error('Error uploading document:', error);
                        this.uploadedPercent = 0;
                    }
                });
        }));
    }

    handleAdvancedDocumentUpload(): void {
        this.openUploadDialog();
    }

    openUploadDialog(): void {
        const dialogRef = this.dialogRef.open(AdvancedFileUploadDialogComponent, {
            width: '400px',
            disableClose: true,
            restoreFocus: false,
            // Props for file upload. All are optional and have defaults if not given.
            data: {
                // Specific to the dialog.
                title: 'Upload',
                closeLabel: 'Close',
                // Pass through to the file upload component.
                allowedFileTypes: documentsAllowedUploadFileTypes,
                categories: this.documentCategories,
                // 20 MB - 1024 * 1024 * 20
                fileSizeLimit: 20971520,
                fileCount: 20,
                fileSelectText: 'Drop files to upload',
                buttonText: 'or choose files'
            },
        });

        // Save documents and show snackbar confirming save success.
        dialogRef.componentInstance.fileListInput.subscribe({
            next: (fileListWithCategories: FileWithCategory[]) => {
                const numberOfFiles = fileListWithCategories.length;
                let numberOfFilesUploaded = 0;
                fileListWithCategories.forEach((fileListItem) => {
                    const formData = new FormData();
                    if (fileListItem.contact) {
                        formData.append('contactId', fileListItem.contact?.id?.toString() ?? '');
                    }
                    if (this?.borrower.id) {
                        formData.append('borrowerId', this.borrower.id.toString());
                        formData.append('category', fileListItem.category);
                        if (fileListItem.category === 'bankStatement') {
                            formData.append('months', JSON.stringify([fileListItem.monthsString!]));
                        }
                        if (DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING.includes(fileListItem.category)) {
                            formData.append('years', JSON.stringify([fileListItem.yearsString]));
                        }
                    }

                    this.uploadProgress = 'Uploading ' + numberOfFilesUploaded + ' of ' + numberOfFiles;
                    this.store.dispatch(new UploadAdvDocument(fileListItem.file, formData))
                        .subscribe({
                            next: () => {
                                numberOfFilesUploaded++;
                                this.uploadProgress = 'Uploading ' + numberOfFilesUploaded + ' of ' + numberOfFiles;
                                this.uploadedPercent = numberOfFilesUploaded / numberOfFiles * 100;
                                if (numberOfFilesUploaded === numberOfFiles) {
                                    this.uploadedPercent = 0;
                                }
                                if (numberOfFilesUploaded >= numberOfFiles) {
                                    dialogRef.close();
                                }
                            },
                            error: (error) => {
                                console.error('Error uploading advanced document:', error);
                                this.uploadedPercent = 0;
                            }
                        });
                });
            },
            error: (error) => {
                console.error('Error in file list input subscription:', error);
            }
        });
    }

    handleCheckOcrolusStatus(documents: Document[]) {
        this.store.dispatch(new GetOcrolusActivity(this.borrower.id, documents));
    }

    dismissOcrolusAlert() {
        this.store.dispatch(new DismissOcrolusAlert(this.borrower.id));
    }

    dismissDocumentAlert() {
        this.store.dispatch(new UpdateDocumentAlert(null));
    }

    handleRepullCredit(borrowerId: number) {
        this.isPullingCredit = true;
        this.documentsService.repullCredit(borrowerId).subscribe({
            next: () => {
                this.isPullingCredit = false;
                this.creditAlert = {
                    level: 'success',
                    message: 'Successfully repulled credit.'
                };
                this.borrower.creditPullStale = false;
                this.borrowerBS.next(this.borrower);
                this.cdRef.markForCheck();
            },
            error: err => {
                this.creditAlert = {
                    level: 'error',
                    message: 'Credit retrieval failed: ' + ((err && err.message) || 'please contact support team')
                };
                this.isPullingCredit = false;
                this.cdRef.markForCheck();

                return err;
            }
        });
    }

    handleExperianCommercialCreditPull(borrowerId: number) {
        this.store.dispatch(new RunExperianCommercial(borrowerId));
        this.cdRef.markForCheck();
    }

    dismissCreditAlert() {
        this.creditAlert = null;
        this.cdRef.markForCheck();
    }
}
